export const formatPrice = (price: number, minimumFractionDigits?: number) =>
	Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: minimumFractionDigits ?? 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
		//maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
	}).format(price);

export const formatCapital = (text: string) => {
	if (!text) return text;
	return text.charAt(0).toUpperCase() + text.slice(1);
};

// format the text from snakecase to normal text
export const formatSnakeCaseToNormalText = (text: string): string => {
	const removedSnakecaseString = text.replaceAll("_", " ");

	return formatCapital(removedSnakecaseString);
};

export const getRangeDateQuery = (
	startDate: string | Date,
	endDate?: string | Date,
) => {
	const start = new Date(startDate);
	const end = new Date(endDate || startDate);
	start.setUTCHours(0, 0, 0, 0);
	end.setUTCHours(23, 59, 0, 0);
	return {
		start: start.toISOString(),
		end: end.toISOString(),
	};
};

/**
 * @params dateString: string
 * @return string
 * @format HH:MM AM|PM
 */
export const getTimeStringFromISO = (dateString: string): string => {
	try {
		const result = new Date(dateString).toLocaleTimeString([], {
			hour: "2-digit",
			minute: "2-digit",
		});
		if (result === "Invalid Date") {
			return dateString;
		}
		return result;
	} catch (err) {
		return dateString;
	}
};

/**
 * @params dateTimeString: string | undefined
 * @return string | undefined
 * @format HH:MM DD/MM/YYYY
 */
export const formatDateTimeString = (
	dateTimeString?: string,
	timeHidden?: boolean,
): string | undefined => {
	if (!dateTimeString) return dateTimeString;

	try {
		const time = new Date(dateTimeString);
		const hours = time.getHours();
		const minutes = time.getMinutes();
		const date = time.getDate();
		const month = time.getMonth() + 1;
		const year = time.getFullYear();

		// local function for format time
		const f = (time: number): string | number => {
			if (time < 10) return `0${time}`;
			return time;
		};

		if (timeHidden) {
			return `${f(date)}/${f(month)}/${year}`;
		} else return `${f(hours)}:${f(minutes)} ${f(date)}/${f(month)}/${year}`;
	} catch (_) {
		return dateTimeString;
	}
};

/**
 * @params dateTimeString: string | undefined
 * @return string | undefined
 * @format DD/MM/YYYY
 */

export const formatDateString = (
	dateTimeString?: string,
): string | undefined => {
	if (!dateTimeString) return dateTimeString;
	try {
		const time = new Date(dateTimeString);
		const date = time.getDate();
		const month = time.getMonth() + 1;
		const year = time.getFullYear();
		return `${date}/${month}/${year}`;
	} catch (_) {
		return dateTimeString;
	}
};

export const formatDateStringMinusLocalTme = (
	dateTimeString?: string,
): string | undefined => {
	if (!dateTimeString) return dateTimeString;
	try {
		const time = new Date(dateTimeString);
		const timeByZoneOffset = new Date(time.getTime() - 7 * 60 * 60 * 1000);
		const date = timeByZoneOffset.getDate();
		const month = timeByZoneOffset.getMonth() + 1;
		const year = timeByZoneOffset.getFullYear();
		return `${date}/${month}/${year}`;
	} catch (_) {
		return dateTimeString;
	}
};

export const formatDateStringMinusLocalTmeYMD = (
	dateTimeString?: string,
): string | undefined => {
	if (!dateTimeString) return dateTimeString;
	try {
		const time = new Date(dateTimeString);
		const timeByZoneOffset = new Date(time.getTime() - 7 * 60 * 60 * 1000);
		const date = timeByZoneOffset.getDate();
		const month = timeByZoneOffset.getMonth() + 1;
		const year = timeByZoneOffset.getFullYear();
		return `${year}/${month}/${date}`;
	} catch (_) {
		return dateTimeString;
	}
};

export const formatDateISOToLocaleDateISO = (isoDateTime: string) => {
	const date = new Date(isoDateTime);
	const timezoneOffset = new Date().getTimezoneOffset();
	const dateLocal = new Date(date.getTime() - timezoneOffset * 60 * 1000);
	return dateLocal.toISOString();
};

// < 10 000: display full number. Ex: 2706
// >= 10 000:
// Using K (thousand), M (million), B (billion)
// Rounded to one decimal place
// Ex: 10 000 → 10,0K; 27 125 000 → 27,1M
// Ex: 123456 -> 123,5K
// NOTE: improve in the future
export const formatNumber = (num: number) => {
	return num
	const abbreviations = ["K", "M", "B", "T"];
	const powers = [1e4, 1e6, 1e9, 1e12];

	if (num < 1e3) {
		return num;
	}

	if (num < powers[0] && num > 1e3) {
		return `${(num / 1e3).toFixed(0)},${num % 1e3}`;
	}

	for (let i = powers.length - 1; i >= 0; i--) {
		if (num >= powers[i]) {
			if (i === 0) {
				return `${(num / 1e3).toFixed(1)}${abbreviations[i]}`;
			} else {
				return `${(num / powers[i]).toFixed(1)}${abbreviations[i]}`;
			}
		}
	}

	return num;
};

/**
 * @params value: string | number
 * @return string
 * @format 123,123
 */
export const numberWithCommas = (value: string | number): string => {
	if (typeof value === "number") value = value.toString();
	return value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const roundedNumber = (value: number, numOfDigits = 2): number =>
	Number(value.toFixed(numOfDigits));

export const trimSpacesBeforeAndAfterWord = (value: string): string =>
	value.replace(/^\s+/, "").replace(/\s+$/, "");

export const formatUSD = (price = 0, minimumFractionDigits?: number) => {
	const res = Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: minimumFractionDigits ?? 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
		maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
	}).format(price);

	return res?.replace("$", "");
};

export const getISOStringWithTime = (
	dateString: string,
	hours: number,
	minutes: number,
	seconds: number,
) => {
	const date = new Date(dateString);
	date.setUTCHours(hours, minutes, seconds, 0);
	return date.toISOString();
};
